/**
 * CogoIframe -- Embeds the /preview entry point in an iframe with postMessage bridge.
 *
 * This component provides CSS isolation by rendering the app in a separate
 * document context (iframe). Studio CSS variables cannot leak into the app
 * and vice versa.
 *
 * Usage:
 * ```tsx
 * const iframeRef = useRef<CogoIframeRef>(null)
 * <CogoIframe ref={iframeRef} onReady={handleReady} onRouteChanged={handleRoute} />
 * iframeRef.current?.loadApp({ manifest, blocks })
 * ```
 */

import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  useCallback,
} from 'react'
import {
  IframeBridge,
  IFRAME_COMMANDS,
  IFRAME_EVENTS,
  createIframeCommand,
  type IframeMessage,
  type LoadAppPayload,
  type AppReadyPayload,
  type RouteChangedPayload,
} from '@cogo/iframe-protocol'
import { STARTER_BASE } from '@cogo/constants'
import { useStudioLocale } from '@/i18n'

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface CogoIframeProps {
  /** CSS class for the iframe wrapper */
  className?: string
  /** URL path for the iframe src (default: '/preview', alt: '/expo-preview') */
  src?: string
  /** Called when iframe app is ready (initial render complete) */
  onReady?: (payload: AppReadyPayload) => void
  /** Called when user navigates within the iframe app */
  onRouteChanged?: (route: string) => void
  /** Called when user selects an element in the iframe (for editor) */
  onElementSelected?: (payload: {
    elementId: string
    blockId: string
    elementType: string
  }) => void
}

export interface CogoIframeRef {
  /** Send app data to iframe for rendering */
  loadApp: (payload: LoadAppPayload) => void
  /** Navigate to a route within the iframe app */
  navigate: (route: string) => void
  /** Set viewport size */
  setViewport: (width: number, height: number) => void
  /** The underlying iframe element — for binding additional host bridges
   * (source/origin validation) to this preview. */
  getIframeElement: () => HTMLIFrameElement | null
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const CogoIframe = forwardRef<CogoIframeRef, CogoIframeProps>(
  function CogoIframe(
    { className, src = STARTER_BASE + '/preview', onReady, onRouteChanged, onElementSelected },
    ref,
  ) {
    const { strings } = useStudioLocale()
    const iframeRef = useRef<HTMLIFrameElement>(null)
    const bridgeRef = useRef<IframeBridge | null>(null)
    const [isLoaded, setIsLoaded] = useState(false)

    // Queue: if loadApp is called before iframe loads, store payload and send on load
    const pendingPayloadRef = useRef<LoadAppPayload | null>(null)
    const lastPayloadRef = useRef<LoadAppPayload | null>(null)
    const loadAppRetryTimersRef = useRef<number[]>([])

    // Stable callback refs -- avoid re-creating bridge when props change
    const onReadyRef = useRef(onReady)
    onReadyRef.current = onReady
    const onRouteChangedRef = useRef(onRouteChanged)
    onRouteChangedRef.current = onRouteChanged
    const onElementSelectedRef = useRef(onElementSelected)
    onElementSelectedRef.current = onElementSelected

    // Send a message to the iframe via the bridge
    const sendToIframe = useCallback((message: IframeMessage) => {
      if (iframeRef.current && bridgeRef.current) {
        bridgeRef.current.sendToIframe(iframeRef.current, message)
      }
    }, [])

    const clearLoadAppRetryTimers = useCallback(() => {
      for (const timer of loadAppRetryTimersRef.current) {
        window.clearTimeout(timer)
      }
      loadAppRetryTimersRef.current = []
    }, [])

    const sendLoadApp = useCallback((payload: LoadAppPayload) => {
      sendToIframe(
        createIframeCommand(
          IFRAME_COMMANDS.LOAD_APP,
          payload as unknown as Record<string, unknown>,
        ),
      )
    }, [sendToIframe])

    const sendLoadAppWithRetry = useCallback((payload: LoadAppPayload) => {
      clearLoadAppRetryTimers()
      sendLoadApp(payload)
      loadAppRetryTimersRef.current = [
        window.setTimeout(() => sendLoadApp(payload), 50),
        window.setTimeout(() => sendLoadApp(payload), 250),
      ]
    }, [clearLoadAppRetryTimers, sendLoadApp])

    // Initialize bridge once on mount
    useEffect(() => {
      const bridge = IframeBridge.forHost()
      // Restrict inbound protocol messages to OUR iframe's window and pin
      // the exchange to its origin (derived from src) — the iframe runs
      // generated app content; without this any window able to post to the
      // Studio top window could forge APP_READY/ROUTE_CHANGED/
      // ELEMENT_SELECTED events.
      if (iframeRef.current) {
        bridge.bindIframe(iframeRef.current)
      }

      // Listen for events from iframe
      const unsubReady = bridge.on(
        IFRAME_EVENTS.APP_READY,
        (msg: IframeMessage) => {
          onReadyRef.current?.(
            msg.payload as unknown as AppReadyPayload,
          )
        },
      )

      const unsubRoute = bridge.on(
        IFRAME_EVENTS.ROUTE_CHANGED,
        (msg: IframeMessage) => {
          const p = msg.payload as unknown as RouteChangedPayload
          onRouteChangedRef.current?.(p.route)
        },
      )

      const unsubSelect = bridge.on(
        IFRAME_EVENTS.ELEMENT_SELECTED,
        (msg: IframeMessage) => {
          onElementSelectedRef.current?.(msg.payload as any)
        },
      )

      bridgeRef.current = bridge

      return () => {
        clearLoadAppRetryTimers()
        unsubReady()
        unsubRoute()
        unsubSelect()
        bridge.destroy()
        bridgeRef.current = null
      }
    }, [])

    // When iframe loads, send pending payload if any
    const handleIframeLoad = useCallback(() => {
      setIsLoaded(true)
      const payload = pendingPayloadRef.current ?? lastPayloadRef.current
      if (payload) {
        sendLoadAppWithRetry(payload)
        pendingPayloadRef.current = null
      }
    }, [sendLoadAppWithRetry])

    // Imperative API via ref
    useImperativeHandle(
      ref,
      () => ({
        loadApp(payload: LoadAppPayload) {
          lastPayloadRef.current = payload
          if (isLoaded && iframeRef.current) {
            sendLoadAppWithRetry(payload)
          } else {
            pendingPayloadRef.current = payload
          }
        },
        navigate(route: string) {
          sendToIframe(
            createIframeCommand(IFRAME_COMMANDS.NAVIGATE, { route }),
          )
        },
        setViewport(width: number, height: number) {
          sendToIframe(
            createIframeCommand(IFRAME_COMMANDS.SET_VIEWPORT, {
              width,
              height,
            }),
          )
        },
        getIframeElement() {
          return iframeRef.current
        },
      }),
      [isLoaded, sendLoadAppWithRetry, sendToIframe],
    )

    return (
      <iframe
        ref={iframeRef}
        src={src}
        title={strings.a11y.appPreviewFrame}
        className={className}
        onLoad={handleIframeLoad}
        style={{ border: 'none', width: '100%', height: '100%' }}
        sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
      />
    )
  },
)
